// Path: ./src/hooks/useUploadOrPickImages.ts
import { useCallback, useEffect, useState, useRef } from "react";
import { adminApiService } from "../utils/api";
import {
  AdvancedSearchConfigDto,
  AdvancedSearchGfImageDto,
  GfImage,
} from "@swagger/typescript-fetch-goflyer";
import { saveSplittedFlyerImage } from "../utils/FlyerImageSave";
import { separateFilesBySize } from "../utils/separateFilesBySize";
import { toastError } from "../utils/pageUtil/toast";
import PSD from "@n.see/psd-parser";
import { convertBase64ToBlob } from "../utils/utils";
import heic2any from "heic2any";
import { useAuthHeaders } from "./useAuthHeaders";
import { useAdvancedSearchConfig } from "./useAdvancedSearchConfig";

/**
 * Custom hook to manage image selection, uploading, and searching functionality for Images.
 *
 * @param onPickedImageChange - Callback function to handle changes to the selected images.
 * @param initialPickedImages - This represents the initial set of images and should be treated as a static value. It should only be updated once, transitioning from `undefined` to its actual value when the data is first available. After this initial update, it should remain unchanged and should not be influenced by any other interactions or by `onPickedImageChange`.
 * @param enableDuplicateImageSelection - Flag to determine whether duplicate image selection is allowed from the search result modal.
 * @param storeIds - Optional array of store IDs to filter images by store when searching.
 * @param keepMimeTypes - File types uploaded as they are instead of being converted, e.g. `["image/gif"]` to keep GIFs animated.
 * @returns An object containing states and handlers for managing the image selection and uploading process.
 */
export const useUploadOrPickImages = ({
  initialPickedImages = [],
  onPickedImageChange,
  enableDuplicateImageSelection = true,
  storeIds,
  dragAndDrop,
  keepMimeTypes,
}: {
  onPickedImageChange: (
    images: GfImage[],
    event?: React.ChangeEvent<HTMLInputElement | DragEvent>,
  ) => void;
  initialPickedImages?: GfImage[];
  enableDuplicateImageSelection?: boolean; // Indicates whether duplicate image selection is allowed from search result modal.
  storeIds?: string[];
  dragAndDrop?: {
    enabled?: boolean;
    defaultPath?: string; // default s3 path used when dropping
    acceptMimeTypes?: string[]; // optional whitelist filter
    onDragStateChange?: (dragging: boolean) => void;
  };
  keepMimeTypes?: string[];
}) => {
  const [gfImagePagination, setGfImagePagination] = useState({
    page: 1,
    perPage: 200,
    total: 0,
  });
  const [hasMoreGfImage, setHasMoreGfImage] = useState(false);
  const [isSearchingImage, setIsSearchingImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [openSearchImage, setOpenSearchImage] = useState(false);
  const [pickedGfImages, setPickedGfImages] =
    useState<GfImage[]>(initialPickedImages);
  const [productImageList, setProductImageList] = useState<GfImage[]>([]);
  const authHeaders = useAuthHeaders();
  const advancedSearchConfig = useAdvancedSearchConfig();
  const [isDragging, setIsDragging] = useState(false);
  const dragEnterCounterRef = useRef(0);

  /**
   * Handles the click event on an image.
   * If duplicate image selection is not enabled, adds the clicked image to the picked images list if it's not already present.
   * If duplicate image selection is enabled, always adds the clicked image to the picked images list.
   * @param image The image object that was clicked.
   */
  const handleOnImageClick = (image: GfImage) => {
    if (!enableDuplicateImageSelection) {
      const found = pickedGfImages.find((val) => val.id === image.id);
      if (!found) {
        setPickedGfImages((prevValue) => {
          onPickedImageChange([...prevValue, image]);
          return [...prevValue, image];
        });
      }
    } else {
      setPickedGfImages((prevValue) => {
        onPickedImageChange([...prevValue, image]);
        return [...prevValue, image];
      });
    }

    handleOnImageSearchModalClose();
  };
  const handleOnImageSearchModalClose = () => {
    setHasMoreGfImage(false);
    // setProductImageList([]);//keep those image even after uses close the modal window
    setGfImagePagination({
      page: 1,
      perPage: 100,
      total: 0,
    });
    setOpenSearchImage(false);
  };
  /**
   * Handles the cancellation of an uploaded image.
   * Removes the image with the specified ID from the list of uploaded images.
   * @param id - The ID of the image to be canceled.
   */
  const handleOnRemoveImage = (id: string) => {
    if (id) {
      if (enableDuplicateImageSelection) {
        const indexToRemove = pickedGfImages.findIndex(
          (val: GfImage) => val.id === id,
        );

        if (indexToRemove !== -1) {
          const updatedGfImages = [
            ...pickedGfImages.slice(0, indexToRemove),
            ...pickedGfImages.slice(indexToRemove + 1),
          ];
          onPickedImageChange(updatedGfImages);
          setPickedGfImages(updatedGfImages);
        }
      } else {
        const filteredGfImages = pickedGfImages?.filter(
          (val: GfImage) => val.id != id,
        );
        onPickedImageChange(filteredGfImages);
        setPickedGfImages(filteredGfImages);
      }
    }
  };

  interface ConversionResult {
    file: File;
    error?: string;
  }
  /**
   * Handles the selection of Images from an input element.
   * Adds selected Images to the list of uploaded images along with unique IDs.
   * @param e - The change event containing the selected Image.
   */
  /**
   * Core processing pipeline shared by input file selection and drag & drop.
   * Accepts a FileList or array of File objects and a destination path.
   */
  const processFiles = useCallback(
    async (
      files: FileList | File[] | null | undefined,
      path = "flyers",
      originEvent?: React.ChangeEvent<HTMLInputElement> | DragEvent,
    ) => {
      if (!files || isUploadingImage) return;
      const fileArray: File[] = Array.from(files as any);

      // Optional MIME type filtering
      const accept = dragAndDrop?.acceptMimeTypes;
      const acceptedFiles =
        accept && accept.length
          ? fileArray.filter((f) => accept.includes(f.type))
          : fileArray;
      const rejectedFiles =
        accept && accept.length
          ? fileArray.filter((f) => !accept.includes(f.type))
          : [];
      if (rejectedFiles.length) {
        rejectedFiles.forEach((f) =>
          toastError(`Rejected ${f.name}: unsupported file type.`),
        );
      }

      const { largeFiles, smallFiles } = separateFilesBySize(
        acceptedFiles as unknown as FileList,
      );
      if (largeFiles?.length) {
        largeFiles.forEach((value) => {
          toastError(
            `Unable to upload ${value.name}: Image size exceeds the 10MB limit.`,
          );
        });
      }
      setIsUploadingImage(true);
      try {
        const results = await Promise.all(
          Array.from(smallFiles).map(convertImage),
        );
        results.forEach(({ error, file: _file }) => {
          if (error) toastError(error);
        });
        const validFilesArray = results
          .filter((r) => !r.error)
          .map((r) => r.file);
        const splitImageResult = await saveSplittedFlyerImage(
          validFilesArray,
          path,
        );
        if (splitImageResult?.length) {
          const gfImagesResponse =
            await adminApiService.createManyBaseGfImageAdminControllerGfImage({
              bulk: splitImageResult,
            });
          setPickedGfImages((prev) => {
            onPickedImageChange(
              [...prev, ...gfImagesResponse],
              originEvent as any,
            );
            return [...prev, ...gfImagesResponse];
          });
        }
      } finally {
        setIsUploadingImage(false);
      }
    },
    [
      dragAndDrop?.acceptMimeTypes,
      isUploadingImage,
      keepMimeTypes,
      onPickedImageChange,
    ],
  );

  /**
   * Public handler for classic input selection
   */
  const handleOnImageFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    path = `flyers`,
  ) => {
    if (!e.target.files) return;
    try {
      await processFiles(e.target.files, path, e);
    } finally {
      // Clear so selecting the same file again fires `change` (browser otherwise keeps same value).
      const input = e.target as HTMLInputElement;
      if (input) input.value = "";
    }
  };

  // ---- Drag & drop reorder modal state ----
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState<{
    files: File[];
    path: string;
  } | null>(null);

  /**
   * Called when user confirms the reorder modal. Uploads files in the chosen order.
   */
  const handleReorderConfirm = async (orderedFiles: File[]) => {
    const path = pendingDropFiles?.path || dragAndDrop?.defaultPath || "flyers";
    setShowReorderModal(false);
    setPendingDropFiles(null);
    await processFiles(orderedFiles, path);
  };

  /**
   * Called when user cancels the reorder modal. Discards the pending drop.
   */
  const handleReorderCancel = () => {
    setShowReorderModal(false);
    setPendingDropFiles(null);
  };

  // ---- Drag & Drop handlers (active only if enabled) ----
  const setDragging = (val: boolean) => {
    setIsDragging(val);
    dragAndDrop?.onDragStateChange?.(val);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!dragAndDrop?.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragEnterCounterRef.current += 1;
    setDragging(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!dragAndDrop?.enabled) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e: React.DragEvent) => {
    if (!dragAndDrop?.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragEnterCounterRef.current -= 1;
    if (dragEnterCounterRef.current <= 0) {
      dragEnterCounterRef.current = 0;
      setDragging(false);
    }
  };
  const handleDrop = async (
    e: React.DragEvent,
    path = dragAndDrop?.defaultPath || "flyers",
  ) => {
    if (!dragAndDrop?.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragEnterCounterRef.current = 0;
    setDragging(false);
    const dt = e.dataTransfer;
    if (dt?.files?.length) {
      // Sort by lastModified as the default order shown in the reorder modal
      const sorted = Array.from(dt.files).sort(
        (a, b) => a.lastModified - b.lastModified,
      );
      if (sorted.length > 1) {
        // Multiple files: let the user reorder before uploading
        setPendingDropFiles({ files: sorted, path });
        setShowReorderModal(true);
      } else {
        // Single file: upload directly
        await processFiles(sorted, path);
      }
    }
  };

  /**
   * Helper to spread onto any drop zone element. Returns no-ops if disabled.
   */
  const getDropZoneProps = (
    overrides: React.HTMLAttributes<HTMLElement> = {},
  ): React.HTMLAttributes<HTMLElement> => {
    if (!dragAndDrop?.enabled) return overrides;
    return {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      ...overrides,
    };
  };
  const convertImage = async (file: File): Promise<ConversionResult> => {
    try {
      const type = file.type.toLowerCase();
      if (keepMimeTypes?.includes(type)) {
        return { file };
      }
      let convertedFile: File = file;
      // HEIC/HEIF conversion
      if (
        type.includes("heic") ||
        type.includes("heif") ||
        file.name.includes(".heic") ||
        file.name.includes(".heif")
      ) {
        const result = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });
        const blob = Array.isArray(result) ? result[0] : result;
        convertedFile = new File(
          [blob],
          file.name.replace(/\.(heic|heif)$/i, ".png"),
          { type: "image/png" },
        );
      }

      // WEBP/RAW/GIF/AVIF conversion
      else if (
        type.includes("webp") ||
        type.includes("raw") ||
        type.includes("gif") ||
        type.includes("avif")
      ) {
        const canvas = document.createElement("canvas");
        const img = await createImageBitmap(file);
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        const blob = await new Promise<Blob>((resolve) =>
          canvas.toBlob((b) => resolve(b!), "image/png", 0.8),
        );
        convertedFile = new File(
          [blob],
          file.name.replace(/\.(webp|raw|gif|avif)$/i, ".png"),
          { type: "image/png" },
        );
      }

      // PSD conversion
      else if (
        type === "image/vnd.adobe.photoshop" ||
        file.name.toLowerCase().endsWith(".psd")
      ) {
        const fileType = "image/png";
        const arrayBuffer = await file.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: file.type });
        const url = URL.createObjectURL(blob);

        const psd = await PSD.fromURL(url);
        const base64String = (psd?.image as any)?.toBase64();
        const base64Data = base64String.replace(
          /^data:image\/(png|jpeg|jpg|psd);base64,/,
          "",
        );
        const saveBlob = convertBase64ToBlob(base64Data, fileType);
        const newFileName = file.name.replace(/\.psd$/i, ".png");

        convertedFile = new File([saveBlob], newFileName, {
          type: fileType,
        });
      }

      return { file: convertedFile };
    } catch (error) {
      return {
        file,
        error: `Failed to convert ${file?.name}: ${(error as Error)?.message}`,
      };
    }
  };
  /**
   * TODO: ideally, the page number should return by the component instead of managing in this hook. Will change in the future.
   */
  const handlePageChangeScroll = useCallback(
    (value: string, advancedConfig?: AdvancedSearchConfigDto) => {
      searchImage(value, gfImagePagination.page, advancedConfig);
    },
    [gfImagePagination.page],
  );

  const searchImage = async (
    value: string,
    page: number,
    advancedConfig?: AdvancedSearchConfigDto,
  ) => {
    //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) why do we need reset here?
    if (value !== "") {
      setHasMoreGfImage(false);
      setIsSearchingImage(true);

      const requestBody: AdvancedSearchGfImageDto = {
        text: `${value}`,
        page,
        limit: gfImagePagination.perPage,
        shouldContain: undefined,
        shouldNotContain: ["/flyers/", "flyers", "/menus/", "menus"],
        mediaType: undefined,
        updatedOrderBy: undefined,
        s3SizeOrderBy: undefined,
        storeIds,
        advancedConfig,
        searchFlyerItems: advancedSearchConfig.searchFlyerItems,
      };

      const response = advancedConfig
        ? await adminApiService.gfImageAdminControllerSearchImageWithAdvancedConfig(
            requestBody,
            authHeaders,
          )
        : await adminApiService.gfImageAdminControllerSearchImageWithFilters(
            requestBody.text || "",
            page,
            gfImagePagination.perPage,
            requestBody.shouldContain,
            requestBody.shouldNotContain,
            requestBody.mediaType,
            requestBody.updatedOrderBy,
            requestBody.s3SizeOrderBy,
            storeIds,
            advancedSearchConfig.searchFlyerItems,
            authHeaders,
          );

      if (response?.count >= gfImagePagination.perPage) {
        setHasMoreGfImage(true);
      }
      setProductImageList((prev) => [...prev, ...response.data]);
      setGfImagePagination({
        ...gfImagePagination,
        page: page + 1,
        total: response?.total,
      });
      setIsSearchingImage(false);
    }
  };

  const handleOnSearchPressEnter = (
    value: string,
    advancedConfig?: AdvancedSearchConfigDto,
  ) => {
    //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) there was a check here gfImagePagination.page === 1
    setProductImageList([]);
    searchImage(value, 1, advancedConfig);
    setHasMoreGfImage(false);
  };
  useEffect(() => {
    /**
     * when open the image search box, show some default images so that users know that they can search images
     */
    searchImage("a", 1);
  }, []);

  const prevInitialPickedImagesRef = useRef<GfImage[]>();

  useEffect(() => {
    // When useUploadOrPickImages is initialized, initialPickedImages might be undefined at first
    // because the page is still fetching data in a separate useEffect. This results in an empty
    // initialPickedImages array, so the hook doesn't have the fetched images when it initializes.
    // By wrapping initialPickedImages in useEffect, we ensure that setPickedGfImages is updated
    // with the correct fetched data once it becomes available.

    // Only update if the content has actually changed (not just the reference)
    const hasChanged =
      !prevInitialPickedImagesRef.current ||
      prevInitialPickedImagesRef.current.length !==
        initialPickedImages.length ||
      JSON.stringify(prevInitialPickedImagesRef.current) !==
        JSON.stringify(initialPickedImages);

    if (hasChanged) {
      setPickedGfImages(initialPickedImages);
      prevInitialPickedImagesRef.current = initialPickedImages;
    }
  }, [initialPickedImages]);

  /**
   * Opens the image modal to search and select an image.
   * Sets the state to indicate that the image search modal is open.
   */
  const handleOnImageSearchModalOpen = () => setOpenSearchImage(true);

  return {
    pickedGfImages,
    productImageList,
    hasMoreGfImage,
    isSearchingImage,
    openSearchImage,
    isUploadingImage,
    handleOnSearchPressEnter,
    handlePageChangeScroll,
    handleOnImageFileSelect,
    handleOnRemoveImage,
    handleOnImageClick,
    handleOnImageSearchModalClose,
    handleOnImageSearchModalOpen,
    setPickedGfImages,
    setProductImageList,
    // Drag & Drop (only meaningful if enabled)
    ...(dragAndDrop?.enabled
      ? {
          isDragging,
          getDropZoneProps,
          handleDrop,
          showReorderModal,
          pendingDropFiles: pendingDropFiles?.files ?? [],
          handleReorderConfirm,
          handleReorderCancel,
        }
      : {}),
  };
};
